import { useRef, useCallback, useState } from 'react';
import Hls from 'hls.js';
import { isSignatureExpiredError } from './streamErrors';

// The camerasv2 platform transcodes on demand: the first playlist request can
// take ~15s to respond, so manifest loading must tolerate the warm-up.
export const HLS_CONFIG = {
  lowLatencyMode: true,
  liveSyncDurationCount: 6,
  liveMaxLatencyDurationCount: 12,
  maxLiveSyncPlaybackRate: 1.0,
  maxBufferLength: 60,
  backBufferLength: 90,
  manifestLoadingTimeOut: 20000,
  manifestLoadingMaxRetry: 4,
  manifestLoadingRetryDelay: 3000,
  manifestLoadingMaxRetryTimeout: 20000,
  fragLoadingMaxRetry: 6,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 8000,
  enableWorker: true,
};

export interface UseCameraStreamOptions {
  // Called when the stream URL signature is rejected (403) or expired (410);
  // the consumer should fetch fresh signed URLs and play again.
  onSignatureExpired?: () => void;
}

export function useCameraStream(
  videoRef: React.RefObject<HTMLVideoElement>,
  options?: UseCameraStreamOptions
) {
  const hlsRef = useRef<Hls | null>(null);
  const [ready, setReady] = useState(false);
  const [hasAudio, setHasAudio] = useState(false);

  const onSignatureExpiredRef = useRef(options?.onSignatureExpired);
  onSignatureExpiredRef.current = options?.onSignatureExpired;

  const destroy = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
    }
    setReady(false);
    setHasAudio(false);
  }, [videoRef]);

  const play = useCallback(async (streamURL: string) => {
    destroy();

    const video = videoRef.current;
    if (!video) return;

    if (video.canPlayType('application/vnd.apple.mpegurl') && !Hls.isSupported()) {
      video.src = streamURL;
      video.addEventListener('loadedmetadata', () => {
        setReady(true);
        const audioTracks = (video as any).audioTracks;
        setHasAudio(audioTracks ? audioTracks.length > 0 : false);
      }, { once: true });
      video.play().catch(console.error);
      return;
    }

    if (!Hls.isSupported()) {
      throw new Error('Browser does not support HLS');
    }

    const hls = new Hls(HLS_CONFIG);

    hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
      setReady(true);
      const audioPresent = (data.levels || []).some(
        (level: any) => level.audioCodec || level.audioGroupIds?.length > 0
      );
      setHasAudio(audioPresent);
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (data?.fatal) {
        if (isSignatureExpiredError(data)) {
          // Retrying the same URL cannot succeed: stop and let the consumer
          // refetch fresh signed URLs.
          hls.destroy();
          hlsRef.current = null;
          onSignatureExpiredRef.current?.();
        }
        else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
        else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
        else { hls.destroy(); hlsRef.current = null; }
      }
    });

    hls.loadSource(streamURL);
    hls.attachMedia(video);
    video.play().catch(console.error);
    hlsRef.current = hls;
  }, [destroy, videoRef]);

  return { play, destroy, ready, hasAudio };
}